
import BooleanRadios from '@/components/forms/BooleanRadios.vue'
import AdmitFields from '@/components/membership/AdmitFields.vue'
import { toKebabCase, toSentenceCase } from '@/helpers/StringHelpers'
import type { LanguageStrings } from '@/language/types'
import { Component, Emit, Prop, Vue, Watch } from 'vue-property-decorator'
import SectionHeader from '@/components/forms/SectionHeader.vue'

@Component({
  name: 'NamedMembers',
  components: { SectionHeader, BooleanRadios, AdmitFields },
})
export default class extends Vue {
  @Prop({ required: true, default: () => [] })
  value: MembershipAdmitDetails[]

  @Prop({ required: true })
  name: string

  @Prop({ required: true })
  type: TicketType

  @Prop({ required: true })
  requiredCount: number

  @Prop({ required: true })
  optionalCount: number

  @Prop({ default: 0 })
  currentCount: number

  @Prop({ required: true })
  emailRequired: MembershipAdmitDetailsConfig

  @Prop({ default: () => new Set() })
  contexts: Set<string>

  nameSingleOptional: boolean | null = null
  optionalSelectedCount: number = 0

  requiredDetails: MembershipAdmitDetails[] = []
  optionalDetails: MembershipAdmitDetails[] = []

  t: LanguageStrings['namedMembers']

  created() {
    // Deep copy this rather than just using this.value to avoid mutating a prop.
    const details = this.value.map((value) => ({ ...value }))
    this.requiredDetails = details.slice(0, this.requiredCount)
    this.optionalDetails = details.slice(this.requiredCount, this.requiredCount + this.optionalCount)
    this.optionalSelectedCount = Math.max(0, this.currentCount - this.requiredCount)
    this.nameSingleOptional = this.optionalSelectedCount === 1
  }

  @Emit('input')
  emit(): MembershipAdmitDetails[] {
    const result = [...this.requiredDetails]
    this.optionalDetails.forEach((details, index) => {
      result.push({
        ...details,
        disabled: index >= this.count,
      })
    })
    return result.map((details) => ({
      ...details,
      email: this.emailRequired !== 'no' ? details.email : undefined,
    }))
  }

  @Watch('nameSingleOptional')
  @Watch('optionalSelectedCount')
  optionalDetailsVisibilityChanged() {
    // Re-emit current values if optional details visibility changes.
    this.emit()
  }

  @Watch('type.id')
  typeChanged() {
    // Re-emit the current value if the Ticket Type changes.
    // This makes it easy for the parent component to maintain current values,
    // even when binding to a dictionary entry keyed by the ticket type ID.
    this.emit()
  }

  get count(): number {
    if (this.optionalCount > 1) {
      return this.optionalSelectedCount
    } else {
      return this.nameSingleOptional ? 1 : 0
    }
  }

  get singular(): string {
    return this.label(1)
  }

  get requiredLabel() {
    const summary = this.type.summary.trim()
    if (summary.length) {
      return summary
    }

    const singular = toSentenceCase(this.singular)
    return this.requiredCount > 1 ? `Please provide the names for each ${this.singular}.` : `${singular} name`
  }

  get optionalLabel() {
    const summary = this.type.summary.trim()
    if (this.requiredCount === 0 && summary.length) {
      return summary
    }

    return this.$t(this.labelKey, { role_name: this.roleName })
  }

  get roleName() {
    if (this.optionalCount === 1) {
      return this.singular
    } else {
      return this.label(2)
    }
  }

  get labelKey() {
    if (this.optionalCount === 1) {
      return this.requiredCount > 0 ? 'namedMembers.singleOptionalLabelAnother' : 'namedMembers.singleOptionalLabel'
    } else {
      return this.requiredCount > 0 ? 'namedMembers.multipleOptionalLabelMore' : 'namedMembers.multipleOptionalLabel'
    }
  }

  /**
   * @deprecated Use the field label instead?
   *
   * TODO Which tenants use this? Could they use the name field label instead?
   */
  get title(): string {
    const giftTitle = this.t.giftTitles[this.roleClassName]
    if (giftTitle && this.contexts.has('gift')) {
      return giftTitle
    } else {
      return this.t.roleTitles[this.roleClassName]
    }
  }

  get roleClassName() {
    return toKebabCase(this.name)
  }

  label(count) {
    const key = `memberRoleLabels.${this.roleClassName}`
    return this.$te(key) ? this.$tc(key, count) : this.type.name
  }
}
